import { useDefinition } from "../../../hooks/useDefinition";
import { ILine } from "../../guideline-overlay";
import { ZoneSelectionBoxHandlers } from "../../zone-selection-box";

enum Corners {
	TopLeft = 0,
	TopRight = 1,
	BottomRight = 2,
	BottomLeft = 3,
}
interface ICornerAndLines {
	cornerId: Corners;
	linesToSnap: IDistanceAndLine[];
}
interface IDistanceAndLine {
	line: eXpress.creator.snap.LineSegment2;
	distance: number;
}

export enum TransformType {
	Move,
	Resize,
}

function getCorners(newRect: eXpress.math.IRectWithAngle) {
	return [
		new eXpress.math.Point(newRect.left, newRect.top),
		new eXpress.math.Point(newRect.left + newRect.width, newRect.top),
		new eXpress.math.Point(newRect.left + newRect.width, newRect.top + newRect.height),
		new eXpress.math.Point(newRect.left, newRect.top + newRect.height),
	];
}
function getRectFromCorners(corners: eXpress.math.Point[]): eXpress.math.Rect {
	const { left, top } = corners[0];
	const width = corners[1].left - left;
	const height = corners[3].top - top;

	return eXpress.math.rect(left, top, width, height);
}

function getLinesInSnappingRange(
	corner: eXpress.math.Point,
	lines: eXpress.creator.snap.LineSegment2[],
): IDistanceAndLine[] {
	const linesInSnappingRange: IDistanceAndLine[] = [];
	lines.forEach((line) => {
		const closestPoint = line.closestPoint({ x: corner.left, y: corner.top }, true);
		const distance = corner.distanceTo({
			left: closestPoint.x,
			top: closestPoint.y,
		});
		if (distance <= line.maxDist) {
			linesInSnappingRange.push({
				line,
				distance,
			} as IDistanceAndLine);
		}
	});

	linesInSnappingRange.sort((prev, curr) => {
		if (prev.distance < curr.distance) return 1;
		return -1;
	});

	return linesInSnappingRange;
}

// get all corners adjacent to the current handle
function getCornersFromHandle(
	_rect: eXpress.math.IRectWithAngle,
	handle: ZoneSelectionBoxHandlers,
): number[] {
	switch (handle) {
		// sideHandles
		case ZoneSelectionBoxHandlers.Top:
			return [Corners.TopLeft, Corners.TopRight];
		case ZoneSelectionBoxHandlers.Right:
			return [Corners.TopRight, Corners.BottomRight];
		case ZoneSelectionBoxHandlers.Bottom:
			return [Corners.BottomRight, Corners.BottomLeft];
		case ZoneSelectionBoxHandlers.Left:
			return [Corners.BottomLeft, Corners.TopLeft];
		// cornerHandles
		case ZoneSelectionBoxHandlers.TopLeft:
			return [Corners.BottomLeft, Corners.TopLeft, Corners.TopRight];
		case ZoneSelectionBoxHandlers.TopRight:
			return [Corners.TopLeft, Corners.TopRight, Corners.BottomRight];
		case ZoneSelectionBoxHandlers.BottomLeft:
			return [Corners.TopLeft, Corners.BottomLeft, Corners.BottomRight];
		case ZoneSelectionBoxHandlers.BottomRight:
			return [Corners.TopRight, Corners.BottomRight, Corners.BottomLeft];
		default:
			return [Corners.TopLeft, Corners.TopRight, Corners.BottomRight, Corners.BottomLeft];
	}
}

// update adjacent corners when resizing from a corner handle
function getRectFromResize(
	corners: eXpress.math.Point[],
	newCorner: eXpress.creator.snap.Vector2 | null | false,
	handle: ZoneSelectionBoxHandlers,
	cornerId: number,
): eXpress.math.Rect {
	if (!newCorner) return getRectFromCorners(corners);
	newCorner = eXpress.creator.snap.Vector2.create(newCorner.x, newCorner.y);

	corners[cornerId] = eXpress.math.point({
		left: newCorner.x,
		top: newCorner.y,
	});
	switch (handle) {
		case ZoneSelectionBoxHandlers.TopLeft:
			corners[Corners.BottomLeft] = eXpress.math.point({
				left: newCorner.x,
				top: corners[3].top,
			});
			corners[Corners.TopRight] = eXpress.math.point({
				left: corners[1].left,
				top: newCorner.y,
			});
			break;
		case ZoneSelectionBoxHandlers.TopRight:
			corners[Corners.TopLeft] = eXpress.math.point({
				left: corners[0].left,
				top: newCorner.y,
			});
			corners[Corners.BottomRight] = eXpress.math.point({
				left: newCorner.x,
				top: corners[2].top,
			});
			break;
		case ZoneSelectionBoxHandlers.BottomRight:
			corners[Corners.TopRight] = eXpress.math.point({
				left: newCorner.x,
				top: corners[1].top,
			});
			corners[Corners.BottomLeft] = eXpress.math.point({
				left: corners[3].left,
				top: newCorner.y,
			});
			break;
		case ZoneSelectionBoxHandlers.BottomLeft:
			corners[Corners.TopLeft] = eXpress.math.point({
				left: newCorner.x,
				top: corners[0].top,
			});
			corners[Corners.BottomRight] = eXpress.math.point({
				left: corners[2].left,
				top: newCorner.y,
			});
			break;
		default:
			return getRectFromCorners(corners);
	}
	return getRectFromCorners(corners);
}

function transformCorner(
	corner: eXpress.math.Point,
	handle: ZoneSelectionBoxHandlers,
	diff: eXpress.math.Point,
): eXpress.math.Point {
	switch (handle) {
		case ZoneSelectionBoxHandlers.Left:
			return eXpress.math.point({ left: diff.left, top: 0 });
		case ZoneSelectionBoxHandlers.Top:
			return eXpress.math.point({ left: 0, top: diff.top });
		case ZoneSelectionBoxHandlers.Right:
			return eXpress.math.point({ left: diff.left, top: 0 });
		case ZoneSelectionBoxHandlers.Bottom:
			return eXpress.math.point({ left: 0, top: diff.top });
		default:
			break;
	}
	return corner;
}

function updateZoneResizeTransform(
	lines: eXpress.creator.snap.LineSegment2[],
	position: eXpress.math.IRectWithAngle,
	handle: ZoneSelectionBoxHandlers | undefined,
	zone: eXpress.core.ITransformableZone | undefined,
): eXpress.math.IRectWithAngle {
	const newRect = {
		left: Math.round(position.left),
		top: Math.round(position.top),
		width: Math.round(position.width),
		height: Math.round(position.height),
		angle: position.angle,
	};

	if (!zone || !handle) {
		return newRect;
	}

	const allCorners = getCorners(newRect);

	const corners = getCornersFromHandle(newRect, handle);

	// list of corners and all lines the corresponding corner can snap to
	const cornersAndSnaps: ICornerAndLines[] = [];
	corners.forEach((cornerId) => {
		// get list of lines for corner where line.maxdist < distance, sorted from closest to furthest
		const linesToSnap = getLinesInSnappingRange(allCorners[cornerId], lines);
		cornersAndSnaps.push({
			cornerId,
			linesToSnap,
		});
	});

	const transformedCorners = getCorners(newRect);
	// filter to get corners with their closest line
	const cornerLines = cornersAndSnaps.map((cs) => ({
		cornerId: cs.cornerId,
		snapLine: cs.linesToSnap.filter((lts) => lts.distance).last(),
	}));

	// get closest distance from any corner to relevant snapping line
	const closestSnap = cornerLines.filter((sd) => sd.snapLine && sd.snapLine.distance).first()
		?.snapLine?.distance;

	// only check for corners that are aligned to the closest line
	const cornersOnSnap = cornerLines.sort((cl) => {
		if (cl.snapLine && closestSnap && cl.snapLine.distance <= closestSnap) return -1;
		return 1;
	});
	let transformedRect = {
		...getRectFromCorners(allCorners),
		angle: position.angle,
	};

	// there should always be 2 corners that have the same line to snap to
	if (cornersOnSnap.length < 2) return position;

	// eslint-disable-next-line no-bitwise
	if (ZoneSelectionBoxHandlers.Sides & handle) {
		cornersOnSnap.forEach((cornerSnap) => {
			const { cornerId, snapLine: linesToSnap } = cornerSnap;
			if (!linesToSnap) return;
			const closestPoint = linesToSnap.line.closestPoint(
				{
					x: transformedCorners[cornerId].left,
					y: transformedCorners[cornerId].top,
				},
				true,
			);
			const closePoint = eXpress.math.point({
				left: closestPoint.x,
				top: closestPoint.y,
			});
			const diff = closePoint.sub(
				transformedCorners[cornerId].left,
				transformedCorners[cornerId].top,
			);

			// transform the corner by changing X or y coordinate, based on the current handle
			transformedCorners[cornerId] = transformedCorners[cornerId].addPoint(
				transformCorner(transformedCorners[cornerId], handle, diff),
			);
			transformedRect = {
				...getRectFromCorners(transformedCorners),
				angle: position.angle,
			};
		});
		// eslint-disable-next-line no-bitwise
	} else if (ZoneSelectionBoxHandlers.Corners & handle) {
		const middleCorner = cornersOnSnap.find(
			// eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
			(cos) => cos.cornerId === corners[1],
		);
		if (!middleCorner) return position;
		const { cornerId, snapLine: linesToSnap } = middleCorner;
		if (!linesToSnap) return position;

		// intersect line from zone center trough snapping corner untill it intersects snapping line
		const rectCenter = zone.rectangle().center();
		const testLine = eXpress.creator.snap.LineSegment2.create(
			eXpress.creator.snap.Vector2.create(rectCenter.left, rectCenter.top),
			eXpress.creator.snap.Vector2.create(
				allCorners[cornerId].left,
				allCorners[cornerId].top,
			),
		);
		const newCorner = linesToSnap.line.intersect(testLine, true);

		transformedRect = {
			...getRectFromResize(allCorners, newCorner, handle, cornerId),
			angle: position.angle,
		};
	}

	return transformedRect;
}
function updateZoneMoveTransform(
	lines: eXpress.creator.snap.LineSegment2[],
	position: eXpress.math.IRectWithAngle,
): eXpress.math.IRectWithAngle {
	const newRect = {
		left: Math.round(position.left),
		top: Math.round(position.top),
		width: Math.round(position.width),
		height: Math.round(position.height),
		angle: position.angle,
	};

	const corners = getCorners(newRect).map((corner) => corner);

	// list of corners and all lines the corresponding corner should snap to
	const cornersAndSnaps: ICornerAndLines[] = [];
	corners.forEach((corner, index) => {
		// get list of lines for corner where line.maxdist < distance, sorted from closest to furthest
		const linesToSnap = getLinesInSnappingRange(corner, lines);
		cornersAndSnaps.push({
			cornerId: index,
			linesToSnap,
		});
	});

	const transformedCorners = corners;
	cornersAndSnaps.forEach((cornerSnap) => {
		const { cornerId, linesToSnap } = cornerSnap;

		// snap corner to corresponding lines in snapping distance
		linesToSnap.forEach((lineToSnap) => {
			const closestPoint = lineToSnap.line.closestPoint(
				{
					x: transformedCorners[cornerId].left,
					y: transformedCorners[cornerId].top,
				},
				true,
			);
			const closePoint = eXpress.math.point({
				left: closestPoint.x,
				top: closestPoint.y,
			});
			const diff = closePoint.sub(
				transformedCorners[cornerId].left,
				transformedCorners[cornerId].top,
			);

			// Move every corner with the distance between current corner and closest point on line,
			// lines are sorted on distance big > small, so the last line we get is the closest one.
			for (let i = 0; i <= 3; i += 1) {
				transformedCorners[i] = transformedCorners[i].addPoint(diff);
			}
		});
	});

	const transformedRect = getRectFromCorners(transformedCorners);
	return {
		...transformedRect,
		angle: position.angle,
	};
}

/**
 * returns the new zoneTransform after snapping
 */
function snapTransform(
	zoneTransform: eXpress.core.ZoneTransform,
	lines: readonly ILine[],
	transformType: TransformType,
	handle?: ZoneSelectionBoxHandlers,
	zone?: eXpress.core.ITransformableZone,
): eXpress.core.ZoneTransform {
	const position: eXpress.math.IRectWithAngle = {
		top: zoneTransform.top - zoneTransform.borderThickness,
		left: zoneTransform.left - zoneTransform.borderThickness,
		width: zoneTransform.width + zoneTransform.borderThickness * 2,
		height: zoneTransform.height + zoneTransform.borderThickness * 2,
		angle: zoneTransform.angle,
	};
	const lineSegments = lines.map((line) => line.lineSegment);
	const newPosition =
		transformType === TransformType.Move
			? updateZoneMoveTransform(lineSegments, position)
			: updateZoneResizeTransform(lineSegments, position, handle, zone);
	const result = zoneTransform.clone();
	if (newPosition !== null) {
		// convert back
		const { borderThickness } = zoneTransform;
		result.top = newPosition.top + borderThickness;
		result.left = newPosition.left + borderThickness;
		result.width = newPosition.width - borderThickness * 2;
		result.height = newPosition.height - borderThickness * 2;
		result.angle = newPosition.angle;
	}

	return result;
}

export function useSnapping() {
	const definition = useDefinition();

	if (!definition) return undefined;

	return (
		newTransform: eXpress.core.ZoneTransform,
		lines: readonly ILine[],
		transformType: TransformType,
		handle?: ZoneSelectionBoxHandlers,
		zone?: eXpress.core.ITransformableZone,
	) => snapTransform(newTransform, lines, transformType, handle, zone);
}
